/**
 * Calculation Results — 2×2 grid with section letters:
 * With reimbursements: (E) Earnings, (R) Reimbursements, (B) Employer, (D) Deductions
 * Without reimbursements: (E), (B) Benefits, (C) Deductions — R panel hidden
 */
import React, { useEffect, useState } from "react";

function fmtMoney(n) {
  const v = Number(n || 0);
  const abs = Math.abs(v).toLocaleString("en-IN");
  return v < 0 ? `−₹${abs}` : `₹${abs}`;
}

function roundMoney(n) {
  return Math.round(Number(n) || 0);
}

function valueTone(n, fallback = "") {
  return Number(n) < 0 ? "text-red-700 font-medium" : fallback;
}

/**
 * MUST stay module-scoped. Defining this inside the parent remounts on every
 * keystroke (new component type) and steals focus after 1–2 digits.
 */
function BreakdownRow({
  label,
  monthly,
  annual,
  strong = false,
  valueClass = "",
  editable = false,
  draftValue = undefined,
  showAnnual = true,
  onMonthlyChange = null,
  labelAddon = null,
  valueHint = "",
  onValueClick = null,
}) {
  const fallback =
    draftValue !== undefined && draftValue !== null
      ? String(draftValue)
      : Number.isFinite(Number(monthly))
        ? String(monthly)
        : "";
  const [focused, setFocused] = useState(false);
  const [local, setLocal] = useState(fallback);

  useEffect(() => {
    if (!focused) setLocal(fallback);
  }, [fallback, focused]);

  const shownMonthly = focused ? local : fallback;
  const shownAnnual = (() => {
    if (editable) {
      const n = Number(String(shownMonthly).replace(/,/g, "").trim());
      if (Number.isFinite(n)) return roundMoney(n * 12);
    }
    return annual;
  })();

  return (
    <div
      className={`grid gap-2 text-sm items-center ${
        showAnnual ? "grid-cols-[1fr_8rem_5.5rem]" : "grid-cols-[1fr_auto]"
      } ${strong ? "font-medium" : ""}`}
    >
      {labelAddon ? (
        <span className="flex min-w-0 items-center gap-1">
          <span className="min-w-0 truncate" title={label}>
            {label}
          </span>
          {labelAddon}
        </span>
      ) : (
        <span className="min-w-0 truncate" title={label}>
          {label}
        </span>
      )}
      {editable && typeof onMonthlyChange === "function" ? (
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={shownMonthly}
          onFocus={(e) => {
            e.stopPropagation();
            setFocused(true);
            setLocal(fallback);
          }}
          onBlur={() => {
            setFocused(false);
            // Commit once on blur so parent recalculation does not fight typing
            onMonthlyChange(local);
          }}
          onChange={(e) => {
            const v = e.target.value;
            if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
            setLocal(v);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          className={`w-full min-w-[7rem] rounded border border-[hsl(var(--ds-border))] bg-white px-1.5 py-1 text-right tabular-nums text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))] ${valueTone(
            Number(shownMonthly) || 0,
            valueClass
          )}`}
          aria-label={`${label} monthly amount`}
        />
      ) : typeof onValueClick === "function" ? (
        <button
          type="button"
          title={valueHint || undefined}
          onClick={onValueClick}
          className={`cursor-pointer rounded text-right tabular-nums underline decoration-dotted decoration-[hsl(var(--ds-muted-fg))] underline-offset-2 hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))] ${valueTone(
            monthly,
            valueClass
          )}`}
        >
          {fmtMoney(monthly)}
        </button>
      ) : (
        <span
          title={valueHint || undefined}
          className={`text-right tabular-nums ${valueHint ? "cursor-help" : ""} ${valueTone(
            monthly,
            valueClass
          )}`}
        >
          {fmtMoney(monthly)}
        </span>
      )}
      {showAnnual ? (
        <span className={`text-right tabular-nums ${valueTone(shownAnnual, valueClass)}`}>
          {fmtMoney(shownAnnual)}
        </span>
      ) : null}
    </div>
  );
}

export default function SalaryStructureCalculationResults({
  earningsList = [],
  benefitsList = [],
  reimbursementsList = [],
  deductionsList = [],
  employerList = [],
  statutoryDeductionsList = [],
  statutoryEmployerList = [],
  totals = {},
  packageMode = "CTC",
  emptyMessage = "Enter CTC above to preview calculation results.",
  showAnnual = true,
  onManualAmountChange = null,
  draftAmounts = null,
  /** { entries, formulaAmounts, overridableIds, canEdit, onOverride(item), onReset(item) } */
  formulaOverride = null,
}) {
  const fmt = fmtMoney;
  const round = roundMoney;
  const isGrossMode = String(packageMode || "CTC").toUpperCase() === "GROSS";
  const profileAmountLabel = isGrossMode ? "Profile Gross" : "Profile CTC";

  const annualOf = (item) =>
    item.annualAmount != null
      ? item.annualAmount
      : round((Number(item.amount) || 0) * 12);



  const earningsRows = [...earningsList, ...benefitsList];

  const earningsSubtotal =

    totals.earningsSubtotal != null

      ? totals.earningsSubtotal

      : round((totals.earnings || 0) + (totals.benefits || 0));

  const earningsSubtotalAnnual =

    totals.earningsSubtotalAnnual != null

      ? totals.earningsSubtotalAnnual

      : round(earningsSubtotal * 12);

  const reimbursementsTotal = round(totals.reimbursements || 0);

  const reimbursementsAnnual =

    totals.reimbursementsAnnual != null

      ? totals.reimbursementsAnnual

      : round(reimbursementsTotal * 12);



  const hasReimbursements = reimbursementsList.length > 0;

  const employerPanelTitle = hasReimbursements

    ? "Employer Contributions (B)"

    : "Benefits (B)";

  const employerTotalLabel = hasReimbursements

    ? "Total Employer (B)"

    : "Total Benefits (B)";

  const deductionsLetter = hasReimbursements ? "D" : "C";

  const deductionsTitle = `Deductions (${deductionsLetter})`;

  const deductionsTotalLabel = `Total Deductions (${deductionsLetter})`;



  const hasData =

    earningsRows.length > 0 ||

    reimbursementsList.length > 0 ||

    deductionsList.length > 0 ||

    employerList.length > 0 ||

    statutoryDeductionsList.length > 0 ||

    statutoryEmployerList.length > 0;



  if (!hasData) {

    return (

      <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-8 text-center">

        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">{emptyMessage}</p>

      </div>

    );

  }



  const resolveDraft = (item) => {
    if (!draftAmounts || typeof draftAmounts !== "object") return undefined;
    const idKey = item.componentId != null ? String(item.componentId) : "";
    const nameKey = String(item.name || "");
    if (idKey && Object.prototype.hasOwnProperty.call(draftAmounts, idKey)) {
      return draftAmounts[idKey];
    }
    if (nameKey && Object.prototype.hasOwnProperty.call(draftAmounts, nameKey)) {
      return draftAmounts[nameKey];
    }
    return undefined;
  };

  /** Hover text + click-to-override for formula rows (no edit icons). */
  const overrideProps = (item) => {
    if (!formulaOverride || item.componentId == null || item.editable) return {};
    const id = String(item.componentId);
    const entry = formulaOverride.entries?.[id];
    const canEdit = !!formulaOverride.canEdit;
    const onValueClick = canEdit ? () => formulaOverride.onOverride?.(item) : null;
    if (entry) {
      const formulaAmt = formulaOverride.formulaAmounts?.[id] ?? entry.formulaAmount;
      const hint = [
        `Overridden for this employee (formula value ${fmtMoney(formulaAmt)}).`,
        entry.reason ? `Reason: ${entry.reason}` : "",
        entry.byName ? `By: ${entry.byName}` : "",
        entry.at ? `On: ${new Date(entry.at).toLocaleString("en-IN")}` : "",
        canEdit
          ? "Click to change the amount or reset to formula."
          : "Needs 'Override Structure Values' access to change.",
      ]
        .filter(Boolean)
        .join("\n");
      return {
        valueHint: hint,
        onValueClick,
        labelAddon: (
          <span
            title={hint}
            className="shrink-0 cursor-help rounded-full bg-[hsl(var(--ds-warning)/0.18)] px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-warning-fg))]"
          >
            Overridden
          </span>
        ),
      };
    }
    if ((formulaOverride.overridableIds || []).includes(id)) {
      return {
        valueHint: canEdit
          ? "Calculated by formula. Click to override for this employee (reason required) — Special Allowance absorbs the difference."
          : "Calculated by formula. Needs 'Override Structure Values' access to change.",
        onValueClick,
      };
    }
    return {
      valueHint: "Balance CTC — absorbs the difference from Profile CTC; not edited directly.",
    };
  };

  const Header = () =>

      showAnnual ? (

      <div className="mb-2 grid grid-cols-[1fr_8rem_5.5rem] gap-2 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">

        <span>Component</span>

        <span className="text-right">Monthly</span>

        <span className="text-right">Annual</span>

      </div>

    ) : null;



  const Panel = ({ title, tone, children, footer, className = "" }) => (

    <div

      className={`flex h-full min-h-[10rem] flex-col rounded-lg border p-3 ${tone} ${className}`}

    >

      <h5 className="mb-2 text-sm font-semibold text-[hsl(var(--ds-fg))]">{title}</h5>

      <Header />

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto">{children}</div>

      {footer ? <div className="mt-2 border-t border-[hsl(var(--ds-border))]/80 pt-2">{footer}</div> : null}

    </div>

  );



  const grossLabel = hasReimbursements ? "Gross Salary (E + R)" : "Gross Salary (E)";

  const netFormulaLabel = hasReimbursements

    ? "Net Pay (N) = E + R − D"

    : "Net Pay (N) = E − C";

  const ctcFormulaLabel = hasReimbursements

    ? "Total CTC (E + R + B)"

    : "Total CTC (E + B)";



  return (

    <div className="space-y-4">

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

        <Panel

          title="Salary Breakdown (E)"

          tone="bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))]"

          footer={

            <BreakdownRow

              label="Subtotal (E)"

              monthly={earningsSubtotal}

              annual={earningsSubtotalAnnual}

              strong

            />

          }

        >

          {earningsRows.length === 0 ? (

            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No earning components.</p>

          ) : (

            earningsRows.map((item) => (
              <BreakdownRow
                key={`earn-${item.name}`}
                label={item.name}
                monthly={item.amount}
                annual={annualOf(item)}
                {...overrideProps(item)}
                editable={!!item.editable && !!onManualAmountChange}
                draftValue={resolveDraft(item)}
                onMonthlyChange={
                  item.editable && onManualAmountChange
                    ? (val) =>
                        onManualAmountChange({
                          name: item.name,
                          componentId: item.componentId,
                          value: val,
                        })
                    : null
                }
              />
            ))

          )}

        </Panel>



        {hasReimbursements ? (

          <Panel

            title="Reimbursements (R)"

            tone="bg-purple-50/80 border-purple-200"

            footer={

              <BreakdownRow

                label="Total Reimbursements (R)"

                monthly={reimbursementsTotal}

                annual={reimbursementsAnnual}

                strong

                valueClass="text-purple-900"

              />

            }

          >

            {reimbursementsList.map((item) => (
              <BreakdownRow
                key={`reimb-${item.name}`}
                label={item.name}
                monthly={item.amount}
                annual={annualOf(item)}
                {...overrideProps(item)}
                editable={!!item.editable && !!onManualAmountChange}
                draftValue={resolveDraft(item)}
                onMonthlyChange={
                  item.editable && onManualAmountChange
                    ? (val) =>
                        onManualAmountChange({
                          name: item.name,
                          componentId: item.componentId,
                          value: val,
                        })
                    : null
                }
              />
            ))}

          </Panel>

        ) : null}



        <Panel

          title={employerPanelTitle}

          tone="bg-[hsl(var(--ds-accent))] border-[hsl(var(--ds-border))]"

          footer={

            <BreakdownRow

              label={employerTotalLabel}

              monthly={totals.employerShares}

              annual={

                totals.employerSharesAnnual ?? round((totals.employerShares || 0) * 12)

              }

              strong

              valueClass="text-[hsl(var(--ds-secondary))]"

            />

          }

        >

          {employerList.length === 0 && statutoryEmployerList.length === 0 ? (

            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No employer contributions.</p>

          ) : (

            <>

              {employerList.map((item) => (

                <BreakdownRow

                  key={`emp-${item.name}`}

                  label={item.name}

                  monthly={item.amount}

                  annual={annualOf(item)}

                />

              ))}

              {statutoryEmployerList.map((item) => (

                <BreakdownRow

                  key={`stat-emp-${item.name}`}

                  label={item.name}

                  monthly={item.amount}

                  annual={annualOf(item)}

                />

              ))}

            </>

          )}

        </Panel>



        <Panel

          title={deductionsTitle}

          tone="bg-red-50 border-red-200"

          className={!hasReimbursements ? "md:col-span-2" : ""}

          footer={

            <BreakdownRow

              label={deductionsTotalLabel}

              monthly={totals.deductions}

              annual={totals.deductionsAnnual ?? round((totals.deductions || 0) * 12)}

              strong

              valueClass="text-red-700"

            />

          }

        >

          {deductionsList.length === 0 && statutoryDeductionsList.length === 0 ? (

            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No deductions.</p>

          ) : (

            <>

              {deductionsList.map((item) => (

                <BreakdownRow

                  key={`ded-${item.name}`}

                  label={item.name}

                  monthly={item.amount}

                  annual={annualOf(item)}

                />

              ))}

              {statutoryDeductionsList.map((item) => (

                <BreakdownRow

                  key={`stat-ded-${item.name}`}

                  label={item.name}

                  monthly={item.amount}

                  annual={annualOf(item)}

                />

              ))}

            </>

          )}

        </Panel>

      </div>



      <div className="rounded-lg border border-green-200 bg-green-50/80 p-3">

        <h5 className="mb-2 text-sm font-semibold text-[hsl(var(--ds-fg))]">Summary</h5>

        <Header />

        <div className="space-y-1.5">

          <BreakdownRow

            label={grossLabel}

            monthly={totals.gross}

            annual={totals.grossAnnual ?? round((totals.gross || 0) * 12)}

          />

          <BreakdownRow

            label={netFormulaLabel}

            monthly={totals.net}

            annual={totals.netAnnual ?? round((totals.net || 0) * 12)}

            strong

            valueClass="text-green-700"

          />

          <BreakdownRow

            label={ctcFormulaLabel}

            monthly={

              totals.actualCtcMonthly ??

              round((totals.gross || 0) + (totals.employerShares || 0))

            }

            annual={

              totals.actualCtcAnnual ??

              round(

                ((totals.actualCtcMonthly != null

                  ? totals.actualCtcMonthly

                  : (totals.gross || 0) + (totals.employerShares || 0)) || 0) * 12

              )

            }

            strong

          />

          {totals.monthlyCtc != null && (

            <BreakdownRow

              label={profileAmountLabel}

              monthly={totals.monthlyCtc}

              annual={totals.annualCtc ?? round((totals.monthlyCtc || 0) * 12)}

            />

          )}

        </div>

      </div>

    </div>

  );

}


